/**
 * Bammies – compact weight-based size selector (blocks/bammies-size-slider.liquid).
 *
 * Moving the slider / typing a weight clicks the product page's EXISTING size button
 * (variant-size-pills or the standard variant picker), so price, variant id, Appstle and
 * the form update exactly as on a manual click. If nothing can be matched, it does nothing.
 *
 * Weight → age mapping mirrors assets/size-guide.js (AGE_MARKERS). Keep them in sync.
 *
 * Open-only sizes (2026-10-10): on a product that lacks some sizes which exist ONLY as open diapers
 * (pull-ups: Mini, Newborn, Explorer), those chart rows are greyed with "Endast öppen blöja". When the
 * weight has no fitting size on this product, no size is picked (no pulse) and a note links to the
 * open-diaper box with the right size preselected. Thresholds come from the size data, not constants.
 *
 * Speed (2026-10-10): the highlight follows the slider instantly (optimistic .bss-hl class on the matching
 * size button, computed on every 'input'), while the real variant change (clicking the theme's size button)
 * is committed once: on 'change'/release, or COMMIT_MS after the last input. A sequence token drops stale
 * commits, and a check after each commit re-applies the selection/highlight if the theme re-rendered.
 */
const COMMIT_MS = 180;

const WEIGHT_MIN = 3;
const WEIGHT_MAX = 20;
const FALLBACK_KG = 8.5;
const STORAGE_KEY = 'bammies_child_kg';
const AB_COOKIE = 'bammies_ss_ab';
const MOBILE_MQ = '(max-width: 749px)';

const AGE_MARKERS = [
  { months: 0, labelKey: 'age_marker_birth', kg: 3, axis: 0 },
  { months: 2, labelKey: 'age_marker_2_mo', kg: 4.87, axis: 11 },
  { months: 5, labelKey: 'age_marker_5_mo', kg: 6.93, axis: 23.1 },
  { months: 10, labelKey: 'age_marker_10_mo', kg: 9.03, axis: 35.5 },
  { months: 24, labelKey: 'age_marker_2_yr', kg: 12.48, axis: 55.8 },
  { months: 48, labelKey: 'age_marker_4_yr', kg: 15.82, axis: 75.4 },
  { months: 72, labelKey: 'age_marker_6_yr', kg: 20, axis: 100 },
];

/** Shared per-product state survives section re-renders (morphing) of the block. */
const STATE = (window.__bammiesSizeSliderState = window.__bammiesSizeSliderState || {});

const interpolate = (tpl, vars = {}) =>
  String(tpl ?? '')
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? `{{ ${k} }}`))
    .replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`))
    .replace(/__(\w+?)__/g, (m, k) => (k in vars ? vars[k] : m)); // Liquid-safe placeholders (t: kg: '__kg__')

const clampKg = (kg) => Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, kg));
const roundHalf = (kg) => Math.round(kg * 2) / 2;
const weightPct = (kg) => ((clampKg(kg) - WEIGHT_MIN) / (WEIGHT_MAX - WEIGHT_MIN)) * 100;

function lerpTable(value, fromKey, toKey) {
  const first = AGE_MARKERS[0][fromKey];
  const last = AGE_MARKERS[AGE_MARKERS.length - 1][fromKey];
  const v = Math.min(last, Math.max(first, value));
  for (let i = 0; i < AGE_MARKERS.length - 1; i++) {
    const a = AGE_MARKERS[i];
    const b = AGE_MARKERS[i + 1];
    if (v >= a[fromKey] && v <= b[fromKey]) {
      const t = (v - a[fromKey]) / (b[fromKey] - a[fromKey] || 1);
      return a[toKey] + t * (b[toKey] - a[toKey]);
    }
  }
  return AGE_MARKERS[AGE_MARKERS.length - 1][toKey];
}
const kgToMonths = (kg) => lerpTable(kg, 'kg', 'months');
const monthsToKg = (m) => lerpTable(m, 'months', 'kg');
const monthsToAxis = (m) => lerpTable(m, 'months', 'axis');
const axisToMonths = (a) => lerpTable(a, 'axis', 'months');

const formatKg = (kg, lang) => {
  const r = roundHalf(kg);
  const s = Number.isInteger(r) ? String(r) : r.toFixed(1);
  return /^(sv|da|nl|de|fi|nb|no)/i.test(lang || '') ? s.replace('.', ',') : s;
};

const RANGE_RE = /(\d+(?:[.,]\d+)?)\s*[-–—]\s*(\d+(?:[.,]\d+)?)\s*kg/i;
function parseRange(text) {
  const m = RANGE_RE.exec(String(text || ''));
  if (!m) return null;
  const min = parseFloat(m[1].replace(',', '.'));
  const max = parseFloat(m[2].replace(',', '.'));
  if (!isFinite(min) || !isFinite(max) || min >= max || min < 1 || max > 40) return null;
  return { min, max };
}
const isPullupText = (text) => /byxbl[öo]j|pull[\s-]?up|luierbroek|bukseble|h[öo]schen/i.test(String(text || ''));
const SIZE_KEYS = ['mini', 'newborn', 'baby', 'toddler', 'kiddo', 'explorer'];
const keyFromText = (text) => {
  const t = String(text || '').toLowerCase();
  return SIZE_KEYS.find((k) => t.includes(k)) || null;
};
const esc = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const shortName = (name) => String(name || '').replace(/^the\s+/i, '');

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/* ---------- consent (Shopify Customer Privacy API) ---------- */
let privacyPromise = null;
function loadPrivacy() {
  if (privacyPromise) return privacyPromise;
  privacyPromise = new Promise((resolve) => {
    const cp = window.Shopify?.customerPrivacy;
    if (cp) return resolve(cp);
    if (typeof window.Shopify?.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) =>
          resolve(err ? null : window.Shopify?.customerPrivacy || null)
        );
        return;
      } catch {
        /* fall through */
      }
    }
    resolve(null);
  });
  return privacyPromise;
}
const safeCall = (fn) => {
  try {
    return typeof fn === 'function' ? !!fn() : false;
  } catch {
    return false;
  }
};
async function consent() {
  const cp = await loadPrivacy();
  if (!cp) return { analytics: false, preferences: false };
  return {
    analytics: safeCall(cp.analyticsProcessingAllowed?.bind(cp)),
    preferences: safeCall(cp.preferencesProcessingAllowed?.bind(cp)),
  };
}

/** Keep third-party widgets that re-render on a variant change (Appstle) at their height until they settle. */
const HOLD_MS = 1000;
function holdWidgetHeight(scope) {
  const els = scope.querySelectorAll('.appstle_sub_widget, .appstle_subscription_wrapper');
  els.forEach((el) => {
    const h = el.getBoundingClientRect().height;
    if (!h) return;
    clearTimeout(el._bssHold);
    if (!el._bssHoldOrig && el._bssHoldOrig !== '') el._bssHoldOrig = el.style.minHeight || '';
    el.style.minHeight = `${Math.max(h, parseFloat(el.style.minHeight) || 0)}px`;
    el._bssHold = setTimeout(() => {
      el.style.minHeight = el._bssHoldOrig;
      delete el._bssHoldOrig;
    }, HOLD_MS);
  });
}

class BammiesSizeSlider extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    try {
      this.cfg = JSON.parse(this.querySelector('[data-bss-config]')?.textContent || '{}');
    } catch {
      this.cfg = {};
    }
    this.lang = document.documentElement.lang || 'sv';
    this.i18n = this.cfg.i18n || {};
    this.settings = this.cfg.settings || {};
    this.sizes = Array.isArray(this.cfg.sizes) ? this.cfg.sizes : [];
    this.names = this.cfg.names || {};
    const pid = String(this.cfg.product?.id || this.dataset.productId || 'x');
    this.state = STATE[pid] = STATE[pid] || { kg: null, mode: 'weight', userOverride: false, open: null };

    this.$ = (sel) => this.querySelector(sel);
    this.range = this.$('[data-bss-range]');
    this.input = this.$('[data-bss-input]');
    this.live = this.$('[data-bss-live]');
    this.openEl = this.$('[data-bss-open]');
    this.openLive = this.$('[data-bss-open-live]');

    this.#computeOpenOnly();
    this.#buildChart();
    this.#bind();
    this.#initOpenState();

    const candidates = this.#candidates();
    const current = candidates.find((c) => c.selected);
    const defaultKg = current ? (current.min + current.max) / 2 : FALLBACK_KG;
    if (this.state.kg == null) this.state.kg = roundHalf(defaultKg);
    this.#render();
    document.fonts?.ready?.then(() => this.#fitInput());
    this.input?.addEventListener('input', () => this.#fitInput());

    consent().then((c) => {
      this.consent = c;
      if ((c.analytics || c.preferences) && this.state.kgFromStorage == null) {
        const stored = parseFloat(localStorage.getItem(STORAGE_KEY) || '');
        if (isFinite(stored) && stored >= WEIGHT_MIN && stored <= WEIGHT_MAX && !this.state.touched) {
          this.state.kgFromStorage = stored;
          this.state.kg = stored;
          this.#render();
          // Restore only the weight. Never change a variant the shopper (or a ?variant= link) chose.
          if (!this.state.userOverride && !new URLSearchParams(location.search).has('variant')) {
            this.#autoSelect({ announce: false });
          }
        }
      }
      this.#setupAbTest();
    });
    document.addEventListener('visitorConsentCollected', () => {
      consent().then((c) => {
        this.consent = c;
        this.#setupAbTest();
      });
    });
  }

  /* ---------- chart ---------- */
  #buildChart() {
    const rowsEl = this.$('[data-bss-rows]');
    if (!rowsEl) return;
    const onlyType = this.dataset.chartRows === 'product_type';
    const wantType = this.cfg.product?.isPullup ? 'pullup' : 'diaper';
    // "all" = one row per size name (open-diaper line covers Mini–Explorer, as in the approved design)
    let rows = this.sizes.filter((s) => s.type === (onlyType ? wantType : 'diaper'));
    if (!rows.length) rows = this.sizes;
    this.chartRows = rows;
    const kgFmt = (n) => formatKg(n, this.lang);
    rowsEl.innerHTML = rows
      .map((s) => {
        const name = this.names[s.key] || s.key;
        const left = weightPct(s.min);
        const width = weightPct(s.max) - left;
        const openOnly = this.openOnlyKeys.has(s.key);
        const tag = openOnly ? `<span class="bss__row-tag">${esc(this.i18n.open_only || 'Endast öppen blöja')}</span>` : '';
        return `<div class="bss__row${openOnly ? ' is-openonly' : ''}" data-size-id="${s.id}">
          <span class="bss__row-label">${esc(name)} · ${kgFmt(s.min)}–${kgFmt(s.max)} kg${tag}</span>
          <span class="bss__row-track"><span class="bss__seg" style="left:${left}%;width:${width}%"></span></span>
        </div>`;
      })
      .join('');
  }

  /* ---------- open-only sizes ---------- */
  #computeOpenOnly() {
    const p = this.cfg.product || {};
    const type = p.isPullup ? 'pullup' : 'diaper';
    // Sizes this product actually has, read from its variants (name first, kg range as fallback).
    const keys = new Set();
    (p.variants || []).forEach((v) => {
      let key = keyFromText(v.title);
      if (!key) {
        const r = parseRange(v.title);
        const hit = r && this.sizes.find((s) => s.type === type && s.min === r.min && s.max === r.max);
        if (hit) key = hit.key;
      }
      if (key) keys.add(key);
    });
    this.productKeys = keys;
    this.productRows = this.sizes.filter((s) => s.type === type && keys.has(s.key));
    // Greyed = missing on this pull-up product AND no pull-up version exists at all (true "open diaper only").
    const pullupKeys = new Set(this.sizes.filter((s) => s.type === 'pullup').map((s) => s.key));
    this.openOnlyKeys = new Set();
    if (p.isPullup && keys.size) {
      this.sizes.forEach((s) => {
        if (s.type === 'diaper' && !keys.has(s.key) && !pullupKeys.has(s.key)) this.openOnlyKeys.add(s.key);
      });
    }
  }

  /** null | { kind: 'also' | 'below' | 'above' | 'gap', alt, edge } */
  #openState(kg) {
    if (!this.openOnlyKeys?.size || !this.productRows?.length) return null;
    const open = this.sizes.filter((s) => s.type === 'diaper' && this.openOnlyKeys.has(s.key) && kg >= s.min && kg <= s.max);
    if (!open.length) return null;
    const sorted = [...open].sort((a, b) => a.min + a.max - (b.min + b.max));
    const alt = (this.settings.ruleDiaper || 'larger') === 'larger' ? sorted[sorted.length - 1] : sorted[0];
    const rows = this.productRows;
    if (rows.some((r) => kg >= r.min && kg <= r.max)) return { kind: 'also', alt };
    const pmin = Math.min(...rows.map((r) => r.min));
    const pmax = Math.max(...rows.map((r) => r.max));
    if (kg < pmin) return { kind: 'below', alt, edge: pmin };
    if (kg > pmax) return { kind: 'above', alt, edge: pmax };
    return { kind: 'gap', alt };
  }

  #openHref(key) {
    const box = this.cfg.openBox;
    if (!box?.url) return null;
    const v = (box.variants || []).find((x) => keyFromText(x.title) === key);
    return v ? `${box.url}?variant=${v.id}` : box.url;
  }

  /**
   * Open-diaper product only: the pull-up box link when kg is within the pull-up range (from the size data).
   * Variant = the pull-up rule (default: the smaller fitting size), among sizes the pull-up box sells.
   */
  #pullupHref(kg) {
    const box = this.cfg.pullupBox;
    if (!box?.url || this.cfg.product?.isPullup) return null;
    const pu = this.sizes.filter((s) => s.type === 'pullup');
    if (!pu.length) return null;
    const lo = Math.min(...pu.map((s) => s.min));
    const hi = Math.max(...pu.map((s) => s.max));
    if (kg < lo || kg > hi) return null;
    const vFor = (key) => {
      const vs = (box.variants || []).filter((x) => keyFromText(x.title) === key);
      return vs.find((x) => x.available) || vs[0] || null;
    };
    const fits = pu.filter((s) => kg >= s.min && kg <= s.max && vFor(s.key)).sort((a, b) => a.min + a.max - (b.min + b.max));
    if (!fits.length) return box.url;
    const pickS = (this.settings.rulePullup || 'smaller') === 'larger' ? fits[fits.length - 1] : fits[0];
    return `${box.url}?variant=${vFor(pickS.key).id}`;
  }

  /** Sizes of this product that fit kg (same pool as #pick), recommended first. null = nothing to list. */
  #fitList(kg) {
    const cands = this.#candidates();
    const res = this.#pick(kg, cands);
    if (res.status !== 'ok' && res.status !== 'soldout') return null;
    const current = cands.find((c) => c.selected);
    const pool = current && cands.some((c) => c.pullup !== current.pullup) ? cands.filter((c) => c.pullup === current.pullup) : cands;
    const fits = pool.filter((c) => kg >= c.min && kg <= c.max && c.key);
    if (!fits.length) return null;
    const rec = res.chosen;
    const rest = fits.filter((c) => c !== rec).sort((a, b) => a.min - b.min);
    return [rec, ...rest].filter((c) => c && c.key);
  }

  /**
   * Message line (v3): one short single-line text, fixed height in every state (CSS), so nothing moves.
   *  - no size of this product fits (under / over its range): the whole line links to the open-diaper box;
   *  - otherwise the between-sizes rule for this product type, darker when two sizes fit, lighter otherwise.
   * The text is only rewritten when the message changes (not per drag frame) and crossfades (opacity).
   * If the full sentence doesn't fit on one line at this width, a shorter version is used (then ellipsis).
   */
  #renderOpen(kg, force) {
    const st = this.#openState(kg);
    const primary = !!st && st.kind !== 'also';
    this.state.openPrimary = primary;
    this.#setNoPick(primary && !this.state.userOverride);
    if (!this.openEl || !this.openEl.classList.contains('bss__sm')) return;
    const line = this.openEl.querySelector('[data-bss-open-msg]');
    if (!line) return;

    let kind, tone, long, short, href = null, also = null;
    if (primary && (st.kind === 'below' || st.kind === 'above')) {
      const edge = formatKg(st.edge, this.lang);
      kind = st.kind;
      tone = 'link';
      long = this.#t(kind === 'below' ? 'msg_below' : 'msg_above', { kg: edge });
      short = kind === 'below' ? this.#t('msg_below_short', { kg: edge }) : long;
      href = this.#openHref(st.alt.key);
    } else {
      const pullup = !!this.cfg.product?.isPullup;
      const list = primary ? null : this.#fitList(kg);
      kind = pullup ? 'tip-p' : 'tip-d';
      tone = list && list.length > 1 ? 'tip' : 'quiet';
      long = this.#t(pullup ? 'msg_tip_pullup' : 'msg_tip_diaper');
      short = this.#t(pullup ? 'msg_tip_pullup_short' : 'msg_tip_diaper_short');
      const puHref = pullup ? null : this.#pullupHref(kg);
      if (puHref) {
        // "Tip · Finns även som byxblöjor ›" – only the second part is a link. Shorter forms, longest first.
        kind = 'tip-d-also';
        href = puHref;
        const lead = this.#t('msg_also_lead'), leadS = this.#t('msg_also_lead_short');
        const link = this.#t('msg_also_pullup'), linkS = this.#t('msg_also_pullup_short');
        also = [[lead, link], [leadS, link], [leadS, linkS], [null, link], [null, linkS]];
        long = `${lead} · ${link}`;
      }
    }
    const sig = `${kind}:${tone}:${href || ''}:${long}`;
    if (!force && sig === this.openEl.dataset.sig) return;
    const first = !this.openEl.dataset.sig;
    this.openEl.dataset.sig = sig;
    const textSig = `${kind}:${long}`;
    const textChanged = this._lineTextSig !== textSig;
    this._lineTextSig = textSig;
    const fade = textChanged && !first && !force && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const build = (text) =>
      Array.isArray(text)
        ? `<span class="bss__sm-text" title="${esc(long)}">${text[0] ? `${esc(text[0])}<span class="bss__sm-sep"> · </span>` : ''}<a class="bss__sm-link" href="${esc(href)}">${esc(text[1])} ›</a></span>`
        : href
        ? `<a class="bss__sm-text bss__sm-link" href="${esc(href)}" title="${esc(long)}">${esc(text)} ›</a>`
        : `<span class="bss__sm-text" title="${esc(long)}">${esc(text)}</span>`;
    const inner = document.createElement('div');
    inner.className = `bss__sm-inner is-${tone}`;
    inner.innerHTML = build(also ? also[0] : long);
    line.querySelectorAll('.is-leaving').forEach((n) => n.remove());
    line.querySelectorAll('.bss__sm-inner').forEach((o) => {
      if (fade) {
        o.classList.add('is-leaving');
        o.setAttribute('aria-hidden', 'true');
        o.inert = true;
        setTimeout(() => o.remove(), 160);
      } else {
        o.remove();
      }
    });
    if (fade) inner.classList.add('is-entering');
    line.appendChild(inner);
    // one line only: use the short version when the full sentence would be cut off at this width
    const over = () => {
      const el = inner.firstElementChild;
      return el && el.scrollWidth > el.clientWidth + 1;
    };
    if (also) {
      for (let i = 1; i < also.length && over(); i++) inner.innerHTML = build(also[i]);
    } else if (short !== long && over()) inner.innerHTML = build(short);
    if (textChanged) this.#announceOpen(textSig, long);
  }

  /** Polite announcement only when the state (sizes / note) changes, after the value has settled. */
  #announceOpen(sig, text) {
    if (!this.openLive) return;
    clearTimeout(this._annT);
    if (this._annSig === undefined) {
      this._annSig = sig; // first render: page load, don't announce
      return;
    }
    this._annT = setTimeout(() => {
      if (sig === this._annSig) return;
      this._annSig = sig;
      this.openLive.textContent = text;
    }, 500);
  }


  /** Copy an ordinary (unselected) size button's look into CSS vars, so the selected one can be drawn plain. */
  #captureNeutral(host, force) {
    if (!force && host.style.getPropertyValue('--bss-np-child-color')) return;
    const ref = host.querySelector('[data-bammies-pills] .SizePill:not(.is-active):not(.bss-hl)');
    if (!ref) return;
    const cs = getComputedStyle(ref);
    host.style.setProperty('--bss-np-bg', cs.backgroundColor);
    host.style.setProperty('--bss-np-border', cs.borderColor);
    host.style.setProperty('--bss-np-color', cs.color);
    const child = ref.querySelector('.pill-line1') || ref.firstElementChild;
    host.style.setProperty('--bss-np-child-color', child ? getComputedStyle(child).color : cs.color);
    const child2 = ref.querySelector('.pill-line2');
    if (child2) host.style.setProperty('--bss-np-child2-color', getComputedStyle(child2).color);
  }

  /** No fitting size here → the selected pill must not look recommended (no red pulse). */
  #setNoPick(on) {
    const s = this.#scope();
    const host = s === document ? document.documentElement : s;
    if (on) {
      this.#captureNeutral(host);
      host.querySelectorAll('.bss-pulse').forEach((el) => el.classList.remove('bss-pulse'));
    }
    host.classList.toggle('bss-no-pick', on);
  }

  /* ---------- open / collapsed ---------- */
  #initOpenState() {
    const mq = window.matchMedia(MOBILE_MQ);
    const startFor = () => (mq.matches ? this.dataset.mobileStart : this.dataset.desktopStart) !== 'collapsed';
    const open = this.state.open ?? startFor();
    this.#setOpen(open, false);
    mq.addEventListener?.('change', () => {
      if (this.state.open == null) this.#setOpen(startFor(), false);
    });
  }
  #setOpen(open, user) {
    if (user) this.state.open = open;
    this.classList.toggle('is-open', open);
    this.classList.toggle('is-collapsed', !open);
    this.$('[data-bss-toggle]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* ---------- events ---------- */
  #bind() {
    this.$('[data-bss-toggle]')?.addEventListener('click', () =>
      this.#setOpen(!this.classList.contains('is-open'), true)
    );

    // Re-fit the one-line message whenever its width changes: window resize AND the panel opening
    // (while collapsed the line has no width, so the long/short choice can only be made once it is visible).
    const msgLine = this.openEl?.querySelector('[data-bss-open-msg]');
    if (msgLine && 'ResizeObserver' in window) {
      let lw = -1;
      new ResizeObserver((entries) => {
        const w = Math.round(entries[entries.length - 1].contentRect.width);
        if (w === lw) return;
        lw = w;
        if (!w) return;
        cancelAnimationFrame(this._rzF);
        this._rzF = requestAnimationFrame(() => this.#renderOpen(this.state.kg ?? FALLBACK_KG, true));
      }).observe(msgLine);
    }

    this.querySelectorAll('[data-bss-mode]').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.state.mode = btn.dataset.bssMode === 'age' ? 'age' : 'weight';
        this.#render();
      })
    );

    if (this.range) {
      this.range.addEventListener('input', () => {
        const v = parseFloat(this.range.value);
        this.state.kg =
          this.state.mode === 'age' ? roundHalf(monthsToKg(axisToMonths(v))) : roundHalf(v);
        this.#userMovedSlider();
      });
      // Debounced selection while dragging: select on 'change' (release / key) and also during input.
      // Release / key / tap: commit right after the highlight has painted (the variant change makes the
      // theme + apps do ~130 ms of work, which must not delay the highlight frame).
      this.range.addEventListener('change', () => this.#commitSoon());
    }

    if (this.input) {
      const commit = () => {
        const v = parseFloat(String(this.input.value).replace(',', '.').replace(/[^\d.]/g, ''));
        if (!isFinite(v)) {
          this.#render();
          return;
        }
        this.state.kg = roundHalf(clampKg(v));
        this.#userMovedSlider();
        this.#commitSoon();
      };
      this.input.addEventListener('change', commit);
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault(); // never submit the product form from here
          commit();
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.state.kg = roundHalf(clampKg((this.state.kg || FALLBACK_KG) + (e.key === 'ArrowUp' ? 0.5 : -0.5)));
          this.#userMovedSlider();
          this.#commitSoon();
        }
      });
    }

    // Any size change (slider commit or manual tap): the Appstle subscription widget below re-renders and
    // briefly collapses (~150 px for ~300 ms), which jumps the add-to-cart button. Hold its height meanwhile.
    if (!STATE.__holdListener) {
      STATE.__holdListener = true;
      document.addEventListener(
        'click',
        (e) => {
          const t = e.target instanceof Element ? e.target.closest('[data-bammies-pills] .SizePill') : null;
          if (t) holdWidgetHeight(t.closest('product-information, section, .shopify-section') || document);
        },
        true
      );
    }

    // Manual clicks on the real size buttons = shopper override. Programmatic clicks are not trusted.
    if (!STATE.__manualListener) {
      STATE.__manualListener = true;
      document.addEventListener(
        'click',
        (e) => {
          if (!e.isTrusted) return;
          const t = e.target instanceof Element ? e.target : null;
          if (!t) return;
          const hit = t.closest('.SizePill, variant-picker input, variant-picker label, .variant-option input, .variant-option label');
          if (!hit) return;
          document.querySelectorAll('bammies-size-slider').forEach((el) => {
            if (typeof el._inScope === 'function' && el._inScope(hit)) el._onManual();
          });
        },
        true
      );
      document.addEventListener(
        'change',
        (e) => {
          if (!e.isTrusted) return;
          const t = e.target;
          if (!(t instanceof HTMLSelectElement) || !t.closest('variant-picker, .variant-option')) return;
          document.querySelectorAll('bammies-size-slider').forEach((el) => {
            if (typeof el._inScope === 'function' && el._inScope(t)) el._onManual();
          });
        },
        true
      );
    }

    // A/B tag on add to cart
    this.#scope().addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.action || '')) return;
        if (this.abGroup) this.#publish('bammies_size_slider_add_to_cart', { group: this.abGroup });
      },
      true
    );
  }

  _inScope(node) {
    return this.#scope().contains(node);
  }
  _onManual() {
    this.state.userOverride = true;
    this.#setNoPick(false); // the shopper picked a size themselves
    clearTimeout(this._t);
    this._seq = (this._seq || 0) + 1; // drop any pending slider commit
    this.#clearHighlight();
    this.#setMsg(null);
    if (this.live) this.live.textContent = '';
  }
  #userMovedSlider() {
    this.state.touched = true;
    this.state.userOverride = false; // moving the slider again hands control back to the slider
    this.#render();
    this.#preview(); // instant highlight, no waiting for the commit
    this.#persist();
    clearTimeout(this._t);
    this._t = setTimeout(() => this.#autoSelect({ announce: true }), COMMIT_MS);
  }

  /** Commit after the next paint (one commit: replaces any pending debounce). */
  #commitSoon() {
    clearTimeout(this._t);
    cancelAnimationFrame(this._cRaf || 0);
    this._cRaf = requestAnimationFrame(() => {
      this._t = setTimeout(() => this.#autoSelect({ announce: true }), 0);
    });
  }

  /* ---------- optimistic highlight ---------- */
  #host() {
    const s = this.#scope();
    return s === document ? document.documentElement : s;
  }
  #hlTargetFor(c) {
    if (!c) return null;
    if (c.select) return c.select;
    return c.el instanceof HTMLInputElement ? c.el.closest('label') || c.el : c.el;
  }
  /** Highlight exactly one size button (or none) right now. Same animation name as .is-active → no restart. */
  #setHighlight(el) {
    const host = this.#host();
    this.#captureNeutral(host); // neutral look for the not-highlighted selected button
    host.querySelectorAll('.bss-hl').forEach((x) => x !== el && x.classList.remove('bss-hl'));
    if (el) el.classList.add('bss-hl');
    host.classList.add('bss-optimistic');
    this._hl = el ? { el, id: el.dataset?.variantId || null } : { el: null, id: null };
    this.#watchRerender();
  }
  #clearHighlight() {
    const host = this.#host();
    host.querySelectorAll('.bss-hl').forEach((x) => x.classList.remove('bss-hl'));
    host.classList.remove('bss-optimistic');
    this._hl = null;
  }
  #preview() {
    if (this.state.userOverride) return;
    const res = this.#pick(this.state.kg, this.#candidates());
    if (res.status === 'nomatch') return;
    this.#setHighlight(res.status === 'ok' ? this.#hlTargetFor(res.chosen) : null);
  }
  /** If the theme replaces the size buttons (morph / section re-render), put the highlight back. */
  #watchRerender() {
    if (this._mo) return;
    const host = this.#host();
    this._mo = new MutationObserver(() => {
      if (this._moRaf) return;
      this._moRaf = requestAnimationFrame(() => {
        this._moRaf = 0;
        const hl = this._hl;
        if (!hl || !hl.el || hl.el.isConnected || !hl.id) return;
        const again = host.querySelector(`[data-bammies-pills] .SizePill[data-variant-id="${CSS.escape(hl.id)}"]`);
        if (again) {
          again.classList.add('bss-hl');
          hl.el = again;
        }
      });
    });
    this._mo.observe(host, { childList: true, subtree: true });
  }
  #formVariantId() {
    const form = this.#host().querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    return form?.querySelector('[name="id"]')?.value || null;
  }
  /** After a commit: make sure the form really holds the chosen variant (theme re-render can revert it). */
  #verify(c, seq, tries = 2) {
    const id = c.el?.dataset?.variantId;
    if (!id) return;
    setTimeout(() => {
      if (seq !== this._seq || this.state.userOverride) return; // a newer commit or a manual pick wins
      if (this.#formVariantId() === id) return;
      const fresh = this.#host().querySelector(`[data-bammies-pills] .SizePill[data-variant-id="${CSS.escape(id)}"]`);
      if (tries > 0 && fresh) {
        fresh.click();
        this.#setHighlight(fresh);
        this.#verify({ el: fresh }, seq, tries - 1);
      } else {
        // give up gracefully: highlight what the form actually holds, so they never disagree
        const actual = this.#host().querySelector(`[data-bammies-pills] .SizePill[data-variant-id="${CSS.escape(this.#formVariantId() || '')}"]`);
        this.#setHighlight(actual || null);
      }
    }, 120);
  }

  #persist() {
    const c = this.consent;
    if (c && (c.analytics || c.preferences)) {
      try {
        localStorage.setItem(STORAGE_KEY, String(this.state.kg));
      } catch {
        /* ignore */
      }
    }
  }

  /* ---------- variant matching ---------- */
  #scope() {
    return (
      this.closest('product-information, .product-information, [data-product-id].product-details, section, .shopify-section') ||
      document
    );
  }

  #sizeByName(text, pullup) {
    const t = String(text || '').toLowerCase();
    const key = ['mini', 'newborn', 'baby', 'toddler', 'kiddo', 'explorer'].find((k) => t.includes(k));
    if (!key) return null;
    return (
      this.sizes.find((s) => s.key === key && s.type === (pullup ? 'pullup' : 'diaper')) ||
      this.sizes.find((s) => s.key === key) ||
      null
    );
  }

  #describe(text, el, available, selected, extra) {
    const productPullup = !!this.cfg.product?.isPullup;
    const pullup = isPullupText(text) || productPullup;
    let r = parseRange(text);
    const byName = this.#sizeByName(text, pullup);
    if (!r && byName) r = { min: byName.min, max: byName.max };
    if (!r) return null;
    return {
      el,
      min: r.min,
      max: r.max,
      pullup,
      available,
      selected,
      name: (byName && this.names[byName.key]) || extra || '',
      key: byName ? byName.key : null,
    };
  }

  #candidates() {
    const scope = this.#scope();
    const out = [];
    const pills = scope.querySelectorAll('[data-bammies-pills] .SizePill');
    if (pills.length) {
      // "Selected" = what the product form really holds (falls back to the pill's class).
      const formId = this.#formVariantId();
      const formHasPill = !!formId && [...pills].some((p) => p.dataset.variantId === formId);
      pills.forEach((p) => {
        const text = `${p.dataset.variantName || ''} ${p.dataset.variantWeight || ''} ${p.textContent || ''}`;
        const c = this.#describe(
          text,
          p,
          p.dataset.variantAvailable !== 'false' && !p.classList.contains('is-soldout'),
          formHasPill ? p.dataset.variantId === formId : p.classList.contains('is-active'),
          (p.dataset.variantDisplay || '').trim()
        );
        if (c) out.push(c);
      });
      return out;
    }

    // Standard theme variant picker (radio buttons). Use the option group that carries kg ranges.
    const fieldsets = scope.querySelectorAll('variant-picker fieldset, .variant-option--buttons');
    for (const fs of fieldsets) {
      const group = [];
      fs.querySelectorAll('input[type="radio"]').forEach((inp) => {
        const label = inp.closest('label');
        const text = `${inp.value} ${label?.textContent || ''}`;
        const c = this.#describe(
          text,
          inp,
          inp.dataset.optionAvailable !== 'false' && inp.getAttribute('aria-disabled') !== 'true',
          inp.checked,
          inp.value
        );
        if (c) group.push(c);
      });
      if (group.length) return group;
    }
    // Dropdowns
    const selects = scope.querySelectorAll('variant-picker select, .variant-option select');
    for (const sel of selects) {
      const group = [];
      [...sel.options].forEach((opt) => {
        const c = this.#describe(opt.textContent || opt.value, opt, !opt.disabled, opt.selected, opt.value);
        if (c) {
          c.select = sel;
          group.push(c);
        }
      });
      if (group.length) return group;
    }
    return out;
  }

  #pick(kg, cands) {
    if (!cands.length) return { status: 'nomatch' };
    // Mixed products (open + pull-up in one): stay within the currently selected type.
    const current = cands.find((c) => c.selected);
    const pool = current && cands.some((c) => c.pullup !== current.pullup) ? cands.filter((c) => c.pullup === current.pullup) : cands;
    const fits = pool.filter((c) => kg >= c.min && kg <= c.max);
    if (!fits.length) return { status: 'nofit' };
    let chosen = fits[0];
    if (fits.length > 1) {
      const pullup = fits[0].pullup;
      const rule = pullup ? this.settings.rulePullup || 'smaller' : this.settings.ruleDiaper || 'larger';
      const sorted = [...fits].sort((a, b) => a.min + a.max - (b.min + b.max));
      chosen = rule === 'larger' ? sorted[sorted.length - 1] : sorted[0];
    }
    if (!chosen.available) return { status: 'soldout', chosen };
    return { status: 'ok', chosen };
  }

  #autoSelect({ announce }) {
    clearTimeout(this._t);
    if (this.state.userOverride) return;
    const seq = (this._seq = (this._seq || 0) + 1);
    const kg = this.state.kg; // always the latest weight → a commit can never apply an old value
    const cands = this.#candidates();
    const res = this.#pick(kg, cands);
    if (res.status === 'nomatch') {
      this.#setMsg(null);
      return; // nothing we can safely match → do nothing
    }
    this.#setHighlight(res.status === 'ok' ? this.#hlTargetFor(res.chosen) : null);
    if (res.status === 'nofit') {
      // The open-diaper note already explains it; otherwise fall back to the generic message.
      this.#setMsg(this.#openState(kg) ? null : 'nofit');
      return;
    }
    if (res.status === 'soldout') {
      this.#setMsg('soldout');
      return;
    }
    this.#setMsg(null);
    const c = res.chosen;
    if (c.selected) return; // already the selected size (checked against the form, not just the class)
    try {
      if (c.select) {
        c.select.value = c.el.value;
        c.select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        c.el.click(); // same code path as a manual click (price, variant id, Appstle)
      }
    } catch {
      return;
    }
    if (c.select || !(c.el instanceof HTMLElement && c.el.classList.contains('SizePill'))) this.#pulse(c);
    this.#verify(c, seq);
    if (announce && this.live) {
      const name = c.name || c.el.textContent?.trim() || '';
      this.live.textContent = interpolate(this.i18n.announce_selected || 'Storlek {size} vald', { size: name });
    }
    this.#publish('bammies_size_slider_select', { kg, size: c.name || '' });
  }

  #pulse(c) {
    // The standard picker can re-render after a click, so look up the selected element again.
    requestAnimationFrame(() => {
      const scope = this.#scope();
      let target = c.el instanceof HTMLInputElement ? c.el.closest('label') || c.el : c.el;
      if (!target.isConnected) {
        target = scope.querySelector('.SizePill.is-active') || scope.querySelector('variant-picker input:checked')?.closest('label');
      }
      if (!target || c.select) target = c.select || target;
      if (!target) return;
      target.classList.remove('bss-pulse');
      void target.offsetWidth;
      target.classList.add('bss-pulse');
      setTimeout(() => target.classList.remove('bss-pulse'), 1600);
    });
  }

  #setMsg(kind) {
    // The suggestion box shows "no size fits" / sold out itself; toggling a message under the chart would move the page.
    if (this.openEl?.classList.contains('bss__sg') && kind) {
      if (kind === 'soldout' && this.live) this.live.textContent = this.i18n.sold_out || '';
      kind = null;
    }
    const nofit = this.$('[data-bss-nofit]');
    const sold = this.$('[data-bss-soldout]');
    if (nofit) nofit.hidden = kind !== 'nofit';
    if (sold) sold.hidden = kind !== 'soldout';
    if (kind === 'soldout' && this.live) this.live.textContent = this.i18n.sold_out || '';
  }

  /* ---------- render ---------- */
  #t(key, vars) {
    return interpolate(this.i18n[key] ?? key, vars);
  }
  #ageRange(months) {
    const m = Math.round(months);
    if (m <= 1) return this.#t('newborn_paren');
    if (m < 24) return this.#t('age_range_months', { lo: Math.max(0, m - 1), hi: m + 1 });
    return this.#t('age_approx_years', { count: Math.round(m / 12) });
  }
  #ageLabel(months) {
    const m = Math.round(months);
    if (m <= 0) return this.#t('newborn');
    if (m < 24) return this.#t('age_months', { count: m });
    return this.#t('age_years', { count: Math.round((m / 12) * 10) / 10 });
  }

  #render() {
    const kg = this.state.kg ?? FALLBACK_KG;
    const months = kgToMonths(kg);
    const age = this.$('[data-bss-age]');
    if (age) age.textContent = this.#ageRange(months);
    if (this.input && document.activeElement !== this.input) {
      this.input.value = formatKg(kg, this.lang);
      this.#fitInput();
    }

    this.querySelectorAll('[data-bss-mode]').forEach((b) => {
      const on = b.dataset.bssMode === this.state.mode;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    let pct;
    if (this.range) {
      if (this.state.mode === 'age') {
        pct = monthsToAxis(months);
        this.range.min = '0';
        this.range.max = '100';
        this.range.step = '0.5';
        this.range.value = String(pct);
        this.range.setAttribute('aria-label', this.#t('aria_child_age'));
        this.range.setAttribute('aria-valuetext', `${this.#ageLabel(months)} (${this.#t('aria_valuetext_kg', { value: formatKg(kg, this.lang) })})`);
      } else {
        pct = weightPct(kg);
        this.range.min = String(WEIGHT_MIN);
        this.range.max = String(WEIGHT_MAX);
        this.range.step = '0.5';
        this.range.value = String(kg);
        this.range.setAttribute('aria-label', this.#t('aria_child_weight'));
        this.range.setAttribute('aria-valuetext', this.#t('aria_valuetext_kg', { value: formatKg(kg, this.lang) }));
      }
      this.range.style.setProperty('--bss-pct', `${pct}%`);
    }

    const ticks = this.$('[data-bss-ticks]');
    if (ticks && ticks.dataset.mode !== this.state.mode) {
      ticks.dataset.mode = this.state.mode;
      if (this.state.mode === 'age') {
        const marks = [AGE_MARKERS[0], AGE_MARKERS[3], AGE_MARKERS[6]];
        ticks.innerHTML = marks.map((m) => `<span>${this.#t(m.labelKey)}</span>`).join('');
      } else {
        ticks.innerHTML = [3, 9, 20].map((v) => `<span>${this.#t('kg_value', { value: v })}</span>`).join('');
      }
    }

    // chart: fits highlighted, marker at current weight (weight axis)
    this.querySelectorAll('.bss__row').forEach((row) => {
      const s = (this.chartRows || []).find((x) => x.id === row.dataset.sizeId);
      row.classList.toggle('is-active', !!s && kg >= s.min && kg <= s.max);
    });
    const marker = this.$('[data-bss-marker]');
    if (marker) marker.style.setProperty('--bss-mark', String(weightPct(kg) / 100));
    this.#renderOpen(kg);
  }

  #fitInput() {
    // Size the kg field to its text so "8,5 kg" reads as one value, like the design.
    try {
      const cs = getComputedStyle(this.input);
      const ctx = (BammiesSizeSlider._ctx ||= document.createElement('canvas').getContext('2d'));
      ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      // Fixed width = the widest value the slider can show (e.g. "18,5"), right-aligned, so "kg" and the
      // age text never move while dragging. Typing a longer value still fits (it only grows, never shrinks).
      // (measured every time: cheap, and stays right after the web font finishes loading)
      let max = 0;
      for (let v = WEIGHT_MIN; v <= WEIGHT_MAX; v += 0.5) max = Math.max(max, ctx.measureText(formatKg(v, this.lang)).width);
      const w = Math.max(max, ctx.measureText(this.input.value || '0').width);
      this.input.style.width = `${Math.ceil(w) + 6}px`;
    } catch {
      /* keep CSS width */
    }
  }

  /* ---------- A/B test (off by default, consent-gated) ---------- */
  #setupAbTest() {
    if (!this.settings.abTestEnabled || this.abGroup) return;
    const c = this.consent || {};
    if (!c.analytics) return; // no consent → block visible, nothing sent
    let group = getCookie(AB_COOKIE);
    if (group !== 'show' && group !== 'hide') {
      group = Math.random() < 0.5 ? 'show' : 'hide';
      const days = Number(this.settings.abCookieDays) || 30;
      document.cookie = `${AB_COOKIE}=${group}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    }
    this.abGroup = group;
    if (group === 'hide') this.hidden = true;
    this.#publish('bammies_size_slider_view', { group });
    this.#tagForm(group);
  }

  #tagForm(group) {
    this.#scope()
      .querySelectorAll('form[action*="/cart/add"]')
      .forEach((form) => {
        if (form.querySelector('input[name="properties[_bammies_size_slider]"]')) return;
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = 'properties[_bammies_size_slider]';
        i.value = group;
        form.appendChild(i);
      });
  }

  #publish(name, data) {
    if (!this.settings.abTestEnabled || !this.abGroup || !(this.consent || {}).analytics) return;
    const payload = { ...data, group: this.abGroup, product_id: this.cfg.product?.id };
    try {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...payload });
    } catch {
      /* ignore */
    }
  }
}

if (!customElements.get('bammies-size-slider')) {
  customElements.define('bammies-size-slider', BammiesSizeSlider);
}
